import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, ReceiptText, ShieldCheck } from "lucide-react";
import { useSettings } from "@/features/settings/contexts/SettingsContext";
import { cn, useTheme } from "@/ui/theme/ThemeContext";

const SALES_EMAIL = "sales@dope-ai.kr";

export const LegalPolicyPage: React.FC = () => {
  const { t } = useSettings();
  const { theme } = useTheme();

  return (
    <div
      data-app-ready="true"
      data-active-view="legal-refund-policy"
      className={cn("min-h-screen px-5 py-8 sm:px-8", theme.bgPrimary, theme.textPrimary)}
    >
      <main className="mx-auto w-full max-w-[880px]">
        <Link
          to="/signup?returnTo=%2F"
          className={cn("inline-flex items-center gap-2 text-sm font-medium underline", theme.infoState)}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t("Back to signup")}
        </Link>

        <section
          className={cn("mt-6 rounded-lg border p-5 shadow-sm sm:p-8", theme.bgSecondary, theme.borderPrimary)}
          aria-labelledby="legal-policy-title"
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", theme.accentBlue)}>
              <ReceiptText className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className={cn("text-sm font-semibold", theme.textPrimary)}>{t("Management Tool")}</p>
              <p className={cn("text-xs", theme.textSecondary)}>{t("Payment and refund policy")}</p>
            </div>
          </div>

          <div className="mt-6 max-w-none">
            <h1 id="legal-policy-title" className={cn("text-2xl font-semibold sm:text-3xl", theme.textPrimary)}>
              {t("Payment and refund policy")}
            </h1>
            <p className={cn("mt-2 text-sm leading-6", theme.textSecondary)}>
              {t("Last updated: May 30, 2026. This policy covers self-service Management Tool subscriptions.")}
            </p>
          </div>

          <div className="mt-7 grid gap-6">
            <PolicySection title={t("Plans and billing")}>
              <p>
                {t("Free is available without payment. Pro and Ultra are monthly account subscriptions. Paid checkout opens only through the configured payment provider, and billing starts only after provider confirmation.")}
              </p>
              <p className="mt-2">
                <Link className={cn("font-medium underline", theme.infoState)} to="/pricing">
                  {t("View public plan details")}
                </Link>
              </p>
            </PolicySection>

            <PolicySection title={t("No hidden charges")}>
              <p>
                {t("The app does not collect card details directly. Stripe Checkout or Toss Payments billing authorization handles payment information on the provider side. Management Tool activates paid billing only after the provider confirms the transaction.")}
              </p>
            </PolicySection>

            <PolicySection title={t("Refund requests")}>
              <p>
                {t("Refund requests should be sent to sales@dope-ai.kr within seven days of the charge. Include the account email, plan, charge date, and reason. Refunds are reviewed against service usage, duplicate charge evidence, failed access, and payment-provider records.")}
              </p>
            </PolicySection>

            <PolicySection title={t("Cancellation")}>
              <p>
                {t("A self-service subscriber may request cancellation before the next monthly renewal. Once the billing portal is enabled, subscribers can manage payment method and cancellation from the provider portal. Until then, cancellation requests are handled by email.")}
              </p>
            </PolicySection>

            <PolicySection title={t("Service period")}>
              <p>
                {t("Self-service Pro and Ultra plans are provided monthly. Access for a paid period remains available until the end of the paid month unless a refund, chargeback, abuse, or account-security issue requires manual review.")}
              </p>
            </PolicySection>

            <PolicySection title={t("Business contact")}>
              <div className="grid gap-3">
                <p>{t("Provider: Dope-AI")}</p>
                <a className={cn("inline-flex items-center gap-2 font-medium underline", theme.infoState)} href={`mailto:${SALES_EMAIL}`}>
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  {SALES_EMAIL}
                </a>
                <p className={cn("text-sm", theme.textSecondary)}>
                  {t("Business registration and ecommerce filing details must match the production footer before live payment launch.")}
                </p>
              </div>
            </PolicySection>

            <div className={cn("flex items-start gap-3 rounded-md border p-4", theme.bgTertiary, theme.borderPrimary)}>
              <ShieldCheck className={cn("mt-0.5 h-5 w-5 shrink-0", theme.infoState)} aria-hidden="true" />
              <p className={cn("text-sm leading-6", theme.textSecondary)}>
                {t("Production billing remains disabled until provider credentials, webhook proof, public business details, and deployment configuration are verified.")}
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

const PolicySection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const { theme } = useTheme();

  return (
    <section className="min-w-0">
      <h2 className={cn("text-base font-semibold", theme.textPrimary)}>{title}</h2>
      <div className={cn("mt-2 text-sm leading-6", theme.textSecondary)}>{children}</div>
    </section>
  );
};
